import { useState, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertTriangle,
  Cpu,
  Globe,
  FileCode,
  Boxes,
  Sparkles,
  Loader2,
  Wrench,
  BookOpen,
  Plug,
  Rocket,
  X,
  Plus,
  Trash2,
  ListChecks,
} from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import AgentBuilderOneShot from '../components/AgentBuilderOneShot';
import GuidedBuildWizard from '../components/GuidedBuildWizard';

// ============================================================
//  Types
// ============================================================
interface LLMConfigForm {
  name: string;
  provider: string;
  model: string;
  api_key: string;
  base_url: string;
  temperature: number;
  max_tokens: number;
}

interface PersonaConfigForm {
  role: string;
  tone: string;
  language: string;
  boundaries: string;
  identity?: { role?: string; domain?: string; experience?: string };
  mission?: { primary?: string; scope?: string[]; out_of_scope?: string[] };
}

interface SkillForm {
  id?: string;
  name: string;
  display_name: string;
  description: string;
  definition: string;
  priority: number;
  is_enabled: boolean;
}

interface ToolOption {
  id: string;
  name: string;
  description: string;
}

interface AttachedTool {
  pt_id?: string;
  tool_id: string;
  name: string;
  alias: string;
  description: string;
  is_enabled: boolean;
  override_config?: string;
}

interface KnowledgeForm {
  id?: string;
  content: string;
  category: string;
}

interface McpServerForm {
  name: string;
  command: string;
  args: string;
  env: string;
}

interface ChannelForm {
  type: string;
  name: string;
  enabled: boolean;
  endpoint?: string;
  app_id?: string;
  app_secret?: string;
  token?: string;
  webhook_url?: string;
  rate_limit?: number;
  extra?: string;
}

// 内置人设模板（/persona/templates 响应项）
interface PersonaTemplateOption {
  id: string | number;
  name: string;
  category: string;
  config?: {
    role?: string;
    tone?: string;
    language?: string;
    boundaries?: string[] | string;
    identity?: { role?: string; domain?: string; experience?: string };
    communication?: { tone?: string };
    mission?: { primary?: string; scope?: string[]; out_of_scope?: string[] };
  };
}

interface WizardState {
  step: number;
  agentName: string;
  agentDesc: string;
  agentDomain: string;
  framework: string;
  agentId: string | null;
  profileId: string | null;
  llmConfigId: string | null;

  llmConfig: LLMConfigForm;
  persona: PersonaConfigForm;
  selectedTemplateId: string | null;

  capabilityTab: string;
  skills: SkillForm[];
  tools: AttachedTool[];
  knowledgeItems: KnowledgeForm[];
  mcpServers: McpServerForm[];

  channels: ChannelForm[];

  deployStatus: string;
  deployError: string | null;
  saving: boolean;
}

// ============================================================
//  Constants
// ============================================================
const INITIAL_STATE: WizardState = {
  step: 0,
  agentName: '',
  agentDesc: '',
  agentDomain: '',
  framework: '',
  agentId: null,
  profileId: null,
  llmConfigId: null,

  llmConfig: {
    name: '默认 LLM 配置',
    provider: 'exgate',
    model: 'gpt-4o',
    api_key: '',
    base_url: 'http://localhost:25500/v1',
    temperature: 0.7,
    max_tokens: 4096,
  },

  persona: {
    role: '',
    tone: 'professional',
    language: 'zh',
    boundaries: '',
  },
  selectedTemplateId: null,

  capabilityTab: 'llm',
  skills: [],
  tools: [],
  knowledgeItems: [],
  mcpServers: [],

  channels: [],

  deployStatus: '',
  deployError: null,
  saving: false,
};

const FRAMEWORKS = [
  {
    id: 'ct_v4',
    name: 'Celestial Toad v4',
    label: 'CT v4',
    icon: Sparkles,
    description: 'agency-agents 框架，支持声明式配置、多通道、子 Agent、MCP',
    deployType: '本地文件 + Docker',
    features: ['Skill 目录结构', '声明式 config.yaml', '多通道(飞书/钉钉/微信)', 'MCP'],
  },
  {
    id: 'hermes',
    name: 'Hermes Agent',
    label: 'Hermes',
    icon: Globe,
    description: 'HTTP API 部署框架，将 Agent 发布为独立服务',
    deployType: 'HTTP API',
    features: ['skill.json', '自包含 agent.py', 'OpenAI/Anthropic 双格式'],
  },
  {
    id: 'openclaw',
    name: 'OpenClaw',
    label: 'OpenClaw',
    icon: FileCode,
    description: 'MCP Tool 格式框架，生成 tool.json + handler.py',
    deployType: '本地文件',
    features: ['MCP 兼容', 'input_schema 定义', '标准 handler 入口'],
  },
  {
    id: 'openhuman',
    name: 'OpenHuman',
    label: 'OpenHuman',
    icon: Bot,
    description: '数字人框架，支持流式响应',
    deployType: 'HTTP API',
    features: ['agent.json', '流式 chat', 'capabilities 动态提取'],
  },
  {
    id: 'generic',
    name: 'Generic',
    label: 'Generic',
    icon: Boxes,
    description: '通用导出框架，输出标准 JSON 能力包',
    deployType: '本地文件 + Docker',
    features: ['essence.json', '分层配置文件', '框架无关'],
  },
];

const STEPS = ['配额检查', '基础信息', '选择框架', '能力配置', '通道配置', '确认部署', '完成'];

const PROVIDER_MODELS: Record<string, string[]> = {
  exgate: ['exgate-default', 'gpt-4o', 'gpt-4o-mini'],
  openai: ['gpt-4o', 'gpt-4o-mini', 'gpt-4.1'],
  anthropic: ['claude-sonnet-4-20250514', 'claude-opus-4-20250514', 'claude-3-5-sonnet-20241022'],
  deepseek: ['deepseek-chat', 'deepseek-reasoner'],
  dashscope: ['qwen-max', 'qwen-coder-plus'],
  moonshot: ['moonshot-v1-8k', 'moonshot-v1-128k'],
  zhipu: ['glm-4', 'glm-4-plus'],
  siliconflow: ['Qwen/Qwen2.5-72B-Instruct', 'deepseek-ai/DeepSeek-V3'],
  volcengine: ['doubao-pro-32k'],
  jdcloud: ['deepseek-r1', 'qwen-max'],
  custom: ['custom'],
};

const DEFAULT_BASE_URLS: Record<string, string> = {
  exgate: 'http://localhost:25500/v1',
  openai: 'https://api.openai.com/v1',
  anthropic: 'https://api.anthropic.com',
  deepseek: 'https://api.deepseek.com/v1',
  dashscope: 'https://dashscope.aliyuncs.com/compatible-mode/v1',
  moonshot: 'https://api.moonshot.cn/v1',
  zhipu: 'https://open.bigmodel.cn/api/paas/v4',
  siliconflow: 'https://api.siliconflow.cn/v1',
  volcengine: 'https://ark.cn-beijing.volces.com/api/v3',
  jdcloud: 'https://modelservice.jdcloud.com/coding/openai/v1',
};

const CT_CHANNEL_TYPES = [
  { id: 'feishu', name: '飞书 Bot', fields: ['app_id', 'app_secret', 'token', 'endpoint'] },
  { id: 'dingtalk', name: '钉钉', fields: ['app_id', 'app_secret', 'token', 'webhook_url'] },
  { id: 'wecom', name: '企业微信', fields: ['app_id', 'app_secret', 'token', 'webhook_url'] },
  { id: 'wechat', name: '微信 Bot', fields: ['token', 'app_id', 'app_secret', 'endpoint'] },
  { id: 'weixin', name: '微信公众号', fields: ['app_id', 'app_secret', 'token', 'endpoint'] },
  { id: 'telegram', name: 'Telegram', fields: ['token', 'endpoint'] },
  { id: 'discord', name: 'Discord', fields: ['token', 'endpoint'] },
  { id: 'slack', name: 'Slack', fields: ['token', 'endpoint'] },
  { id: 'email', name: 'Email', fields: ['endpoint', 'extra'] },
  { id: 'webhook', name: 'Webhook', fields: ['endpoint', 'token'] },
  { id: 'grpc', name: 'gRPC', fields: ['endpoint', 'extra'] },
];

// ============================================================
//  Reducer
// ============================================================
type WizardAction =
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'SET_FIELD'; field: keyof WizardState; value: unknown }
  | { type: 'SET_NESTED'; parent: keyof WizardState; field: string; value: unknown }
  | { type: 'SET_AGENT'; agentId: string; profileId: string; llmConfigId?: string | null }
  | { type: 'SET_CAPABILITY_TAB'; payload: string }
  | { type: 'SET_PERSONA'; payload: Partial<PersonaConfigForm> }
  | { type: 'SET_LLM'; payload: Partial<LLMConfigForm> }
  | { type: 'SET_SKILLS'; payload: SkillForm[] }
  | { type: 'SET_TOOLS'; payload: AttachedTool[] }
  | { type: 'SET_KNOWLEDGE'; payload: KnowledgeForm[] }
  | { type: 'SET_MCP'; payload: McpServerForm[] }
  | { type: 'SET_CHANNELS'; payload: ChannelForm[] }
  | { type: 'SET_DEPLOY_STATUS'; payload: string }
  | { type: 'SET_DEPLOY_ERROR'; payload: string | null }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'RESET' };

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'NEXT_STEP':
      return { ...state, step: Math.min(state.step + 1, STEPS.length - 1) };
    case 'PREV_STEP':
      return { ...state, step: Math.max(state.step - 1, 0) };
    case 'SET_STEP':
      return { ...state, step: action.payload };
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value } as WizardState;
    case 'SET_NESTED':
      return {
        ...state,
        [action.parent]: {
          ...(state[action.parent] as unknown as Record<string, unknown>),
          [action.field]: action.value,
        },
      } as WizardState;
    case 'SET_AGENT':
      return {
        ...state,
        agentId: action.agentId,
        profileId: action.profileId,
        llmConfigId: action.llmConfigId || null,
      };
    case 'SET_CAPABILITY_TAB':
      return { ...state, capabilityTab: action.payload };
    case 'SET_PERSONA':
      return { ...state, persona: { ...state.persona, ...action.payload } };
    case 'SET_LLM':
      return { ...state, llmConfig: { ...state.llmConfig, ...action.payload } };
    case 'SET_SKILLS':
      return { ...state, skills: action.payload };
    case 'SET_TOOLS':
      return { ...state, tools: action.payload };
    case 'SET_KNOWLEDGE':
      return { ...state, knowledgeItems: action.payload };
    case 'SET_MCP':
      return { ...state, mcpServers: action.payload };
    case 'SET_CHANNELS':
      return { ...state, channels: action.payload };
    case 'SET_DEPLOY_STATUS':
      return { ...state, deployStatus: action.payload };
    case 'SET_DEPLOY_ERROR':
      return { ...state, deployError: action.payload };
    case 'SET_SAVING':
      return { ...state, saving: action.payload };
    case 'RESET':
      return INITIAL_STATE;
    default:
      return state;
  }
}

// ============================================================
//  Helpers
// ============================================================
function generateName(type: string, index: number) {
  return `${type}-${index + 1}`;
}

function emptySkill(): SkillForm {
  return {
    name: '',
    display_name: '',
    description: '',
    definition: '',
    priority: 5,
    is_enabled: true,
  };
}

function emptyChannel(type: string): ChannelForm {
  return {
    type,
    name: generateName(type, 0),
    enabled: true,
    rate_limit: 100,
  };
}

function emptyMcp(): McpServerForm {
  return { name: '', command: 'npx', args: '', env: '' };
}

// ============================================================
//  Main Component
// ============================================================
export default function CreateAgentWizard() {
  const [state, dispatch] = useReducer(wizardReducer, INITIAL_STATE);
  // 创建模式：一句话创建（P6 对话式 Builder）/ 引导式创建（B1b 五阶段）/ 表单向导
  const [mode, setMode] = useState<'oneshot' | 'guided' | 'wizard'>('oneshot');
  const [quotaChecked, setQuotaChecked] = useState(false);
  const [quotaOk, setQuotaOk] = useState(false);
  const [availableTools, setAvailableTools] = useState<ToolOption[]>([]);
  const [personaTemplates, setPersonaTemplates] = useState<PersonaTemplateOption[]>([]);
  const navigate = useNavigate();
  const toast = useToast();

  // 挂载时并行拉取配额/工具/人设模板；内联 promise 链避免 effect 内同步 setState
  useEffect(() => {
    apiClient
      .get('/admin/sandboxes/stats')
      .then(() => setQuotaOk(true))
      .catch(() => setQuotaOk(true))
      .finally(() => setQuotaChecked(true));
    apiClient
      .get('/tools')
      .then((res) => setAvailableTools(res.data || []))
      .catch(() => setAvailableTools([]));
    apiClient
      .get('/persona/templates')
      .then((res) => setPersonaTemplates(res.data || []))
      .catch(() => setPersonaTemplates([]));
  }, []);

  // 创建并部署成功后自动跳转到 Agent 详情页
  useEffect(() => {
    if (state.step === 6 && state.deployStatus === 'completed' && state.agentId) {
      const timer = setTimeout(() => {
        navigate(`/agents/${state.agentId}`);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [state.step, state.deployStatus, state.agentId, navigate]);

  const canProceed = () => {
    switch (state.step) {
      case 0:
        return quotaOk;
      case 1:
        return state.agentName.trim().length > 0;
      case 2:
        return state.framework !== '';
      case 3:
        return (
          state.llmConfig.provider !== '' &&
          state.llmConfig.model !== '' &&
          state.llmConfig.name.trim().length > 0 &&
          state.llmConfig.api_key.trim().length > 0 &&
          (state.llmConfig.provider !== 'custom' || state.llmConfig.base_url.trim().length > 0)
        );
      case 4:
        return true;
      case 5:
        return true;
      case 6:
        return true;
      default:
        return false;
    }
  };

  const handleCreateAgentAndProfile = async () => {
    dispatch({ type: 'SET_SAVING', payload: true });
    try {
      const res = await apiClient.post('/agents/with-profile', {
        name: state.agentName,
        description: state.agentDesc,
        domain: state.agentDomain,
        framework: state.framework,
        llm_config: {
          ...state.llmConfig,
          provider: state.llmConfig.provider,
        },
        persona_config: state.persona,
      });
      const { agent, profile_id, llm_config_id } = res.data;
      dispatch({ type: 'SET_AGENT', agentId: agent.id, profileId: profile_id, llmConfigId: llm_config_id });
      dispatch({ type: 'NEXT_STEP' });
      toast.success('Agent 与 Profile 创建成功');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const message =
        (typeof detail === 'string' ? detail : '') ||
        (typeof err.message === 'string' ? err.message : '');
      toast.error('创建失败: ' + message);
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  };

  const saveWizardConfig = async (extra?: Record<string, unknown>) => {
    if (!state.profileId) return;
    dispatch({ type: 'SET_SAVING', payload: true });
    try {
      await apiClient.put(`/profiles/${state.profileId}/wizard-config`, {
        llm_config_id: state.llmConfigId,
        persona_config: state.persona,
        mcp_servers: state.mcpServers.map((s) => ({
          name: s.name || 'mcp-server',
          command: s.command,
          args: s.args.split(',').map((a) => a.trim()).filter(Boolean),
          env: s.env
            ? Object.fromEntries(
                s.env
                  .split('\n')
                  .map((line) => line.split('='))
                  .filter(([k, v]) => k && v)
              )
            : {},
        })),
        channels: state.channels,
        extra,
      });
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const message =
        (typeof detail === 'string' ? detail : '') ||
        (typeof err.message === 'string' ? err.message : '');
      toast.error('保存配置失败: ' + message);
      throw e;
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  };

  const saveSkills = async () => {
    if (!state.profileId) return;
    for (const skill of state.skills) {
      if (!skill.name.trim()) continue;
      await apiClient.post(`/profiles/${state.profileId}/skills`, {
        name: skill.name,
        display_name: skill.display_name || skill.name,
        description: skill.description,
        definition: skill.definition,
        priority: skill.priority,
        is_enabled: skill.is_enabled,
        source: 'manual',
      });
    }
  };

  const saveTools = async () => {
    if (!state.agentId) return;
    for (const t of state.tools) {
      await apiClient.post(`/agents/${state.agentId}/tools`, {
        name: t.name,
        description: t.description,
        alias: t.alias || t.name,
        is_enabled: t.is_enabled,
      });
    }
  };

  const saveKnowledge = async () => {
    if (!state.agentId) return;
    for (const item of state.knowledgeItems) {
      if (!item.content.trim()) continue;
      await apiClient.post(`/agents/${state.agentId}/knowledge?auto_vectorize=true`, {
        content: item.content,
        category: item.category || 'general',
        source_type: 'wizard',
        confidence: 1.0,
      });
    }
  };

  const handleNext = async () => {
    if (state.step === 2) {
      await handleCreateAgentAndProfile();
      return;
    }
    if (state.step === 3) {
      // Persist capability config before leaving
      await saveWizardConfig();
      await saveSkills();
      await saveTools();
      await saveKnowledge();
      toast.success('能力配置已保存');
    }
    if (state.step === 4) {
      await saveWizardConfig();
      toast.success('通道配置已保存');
    }
    dispatch({ type: 'NEXT_STEP' });
  };

  const doDeploy = async () => {
    if (!state.agentId) return;
    dispatch({ type: 'SET_DEPLOY_STATUS', payload: 'deploying' });
    try {
      await apiClient.post(`/agents/${state.agentId}/deploy?framework=${state.framework}`);
      dispatch({ type: 'SET_DEPLOY_STATUS', payload: 'completed' });
      toast.success('部署请求已提交');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
      const detail = err.response?.data?.detail;
      const message =
        (typeof detail === 'string' ? detail : '') ||
        (typeof err.message === 'string' ? err.message : '');
      dispatch({ type: 'SET_DEPLOY_STATUS', payload: 'failed' });
      dispatch({ type: 'SET_DEPLOY_ERROR', payload: message });
      toast.error('部署失败: ' + message);
    }
  };

  // ============================================================
  //  Render
  // ============================================================
  const renderStepIndicator = () => (
    <div className="wizard-steps">
      {STEPS.map((label, idx) => (
        <div key={label} className={`wizard-step ${idx === state.step ? 'active' : ''} ${idx < state.step ? 'done' : ''}`}>
          <div className="wizard-step-number">{idx < state.step ? <Check size={14} /> : idx + 1}</div>
          <div className="wizard-step-label">{label}</div>
        </div>
      ))}
    </div>
  );

  const renderStep0 = () => (
    <div className="animate-fade-in">
      <h3 style={{ marginBottom: '16px' }}>配额检查</h3>
      {!quotaChecked ? (
        <div className="loading"><Loader2 size={24} className="spin" /> 检查中...</div>
      ) : quotaOk ? (
        <div className="glass-card neon-border-green" style={{ textAlign: 'center', padding: '40px' }}>
          <Check size={48} style={{ color: 'var(--neon-green)', marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--neon-green)' }}>配额充足</h3>
          <p style={{ color: 'var(--text-secondary)' }}>当前可创建新的 Agent，点击「下一步」继续</p>
        </div>
      ) : (
        <div className="glass-card neon-border" style={{ textAlign: 'center', padding: '40px', borderColor: 'var(--color-error)' }}>
          <AlertTriangle size={48} style={{ color: 'var(--color-error)', marginBottom: '16px' }} />
          <h3 style={{ color: 'var(--color-error)' }}>配额不足</h3>
          <p>已达到 Agent 创建上限，请联系管理员提升配额</p>
        </div>
      )}
    </div>
  );

  const renderStep1 = () => (
    <div className="animate-fade-in">
      <h3 style={{ marginBottom: '16px' }}>基础信息</h3>
      <div className="flex flex-col" style={{ gap: '16px' }}>
        <div>
          <label className="form-label">Agent 名称 <span style={{ color: 'var(--color-error)' }}>*</span></label>
          <input
            value={state.agentName}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'agentName', value: e.target.value })}
            placeholder="给你的 Agent 起个名字"
            maxLength={64}
          />
        </div>
        <div>
          <label className="form-label">描述</label>
          <textarea
            value={state.agentDesc}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'agentDesc', value: e.target.value })}
            placeholder="这个 Agent 的职责和能力是什么？"
            rows={3}
          />
        </div>
        <div>
          <label className="form-label">领域</label>
          <input
            value={state.agentDomain}
            onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'agentDomain', value: e.target.value })}
            placeholder="例如：客服、技术支持、内容创作"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="animate-fade-in">
      <h3 style={{ marginBottom: '16px' }}>选择目标框架</h3>
      <div className="framework-grid">
        {FRAMEWORKS.map((fw) => {
          const Icon = fw.icon;
          const selected = state.framework === fw.id;
          return (
            <div
              key={fw.id}
              className={`framework-card ${selected ? 'selected' : ''}`}
              onClick={() => dispatch({ type: 'SET_FIELD', field: 'framework', value: fw.id })}
            >
              <div className="framework-icon"><Icon size={28} /></div>
              <h4>{fw.name}</h4>
              <p>{fw.description}</p>
              <div className="framework-features">
                {fw.features.map((f) => <span key={f} className="feature-tag">{f}</span>)}
              </div>
              <div className="framework-deploy">{fw.deployType}</div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---------------- Step 3: Capability Tabs ----------------
  const renderLLMForm = () => (
    <div className="flex flex-col" style={{ gap: '14px' }}>
      <div>
        <label className="form-label">配置名称 <span style={{ color: 'var(--color-error)' }}>*</span></label>
        <input
          value={state.llmConfig.name}
          onChange={(e) => dispatch({ type: 'SET_LLM', payload: { name: e.target.value } })}
          placeholder="例如: GPT-4o 生产配置"
        />
      </div>
      <div className="grid grid-2" style={{ gap: '14px' }}>
        <div>
          <label className="form-label">Provider <span style={{ color: 'var(--color-error)' }}>*</span></label>
          <select
            value={state.llmConfig.provider}
            onChange={(e) => {
              const p = e.target.value;
              dispatch({
                type: 'SET_LLM',
                payload: {
                  provider: p,
                  base_url: DEFAULT_BASE_URLS[p] || '',
                  model: PROVIDER_MODELS[p]?.[0] || 'custom',
                },
              });
            }}
          >
            <option value="exgate">ExGate</option>
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic</option>
            <option value="deepseek">DeepSeek</option>
            <option value="dashscope">阿里云 DashScope</option>
            <option value="moonshot">月之暗面</option>
            <option value="zhipu">智谱</option>
            <option value="siliconflow">硅基流动</option>
            <option value="volcengine">火山引擎</option>
            <option value="jdcloud">京东云</option>
            <option value="custom">自定义</option>
          </select>
        </div>
        <div>
          <label className="form-label">Model <span style={{ color: 'var(--color-error)' }}>*</span></label>
          <select
            value={state.llmConfig.model}
            onChange={(e) => dispatch({ type: 'SET_LLM', payload: { model: e.target.value } })}
          >
            {(PROVIDER_MODELS[state.llmConfig.provider] || ['custom']).map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="form-label">API Key <span style={{ color: 'var(--color-error)' }}>*</span></label>
        <input
          type="password"
          value={state.llmConfig.api_key}
          onChange={(e) => dispatch({ type: 'SET_LLM', payload: { api_key: e.target.value } })}
          placeholder={state.llmConfig.provider === 'exgate' ? 'ExGate 虚拟 Key' : '模型服务商 API Key'}
        />
      </div>
      <div>
        <label className="form-label">Base URL {state.llmConfig.provider === 'custom' && <span style={{ color: 'var(--color-error)' }}>*</span>}</label>
        <input
          value={state.llmConfig.base_url}
          onChange={(e) => dispatch({ type: 'SET_LLM', payload: { base_url: e.target.value } })}
          placeholder="OpenAI 兼容接口地址"
        />
      </div>
      <div className="grid grid-2" style={{ gap: '14px' }}>
        <div>
          <label className="form-label">Temperature: {state.llmConfig.temperature}</label>
          <input
            type="range"
            min={0}
            max={2}
            step={0.1}
            value={state.llmConfig.temperature}
            onChange={(e) => dispatch({ type: 'SET_LLM', payload: { temperature: parseFloat(e.target.value) } })}
          />
        </div>
        <div>
          <label className="form-label">Max Tokens</label>
          <input
            type="number"
            value={state.llmConfig.max_tokens}
            onChange={(e) => dispatch({ type: 'SET_LLM', payload: { max_tokens: parseInt(e.target.value) || 0 } })}
          />
        </div>
      </div>
    </div>
  );

  const renderPersonaForm = () => (
    <div className="flex flex-col" style={{ gap: '14px' }}>
      <div>
        <label className="form-label">选择内置人设模板</label>
        <select
          value={state.selectedTemplateId || ''}
          onChange={(e) => {
            const id = e.target.value || null;
            dispatch({ type: 'SET_FIELD', field: 'selectedTemplateId', value: id });
            if (id) {
              const tmpl = personaTemplates.find((t) => String(t.id) === id);
              if (tmpl?.config) {
                const cfg = tmpl.config;
                dispatch({
                  type: 'SET_PERSONA',
                  payload: {
                    role: cfg.role || cfg.identity?.role || '',
                    tone: cfg.tone || cfg.communication?.tone || 'professional',
                    language: cfg.language || 'zh',
                    boundaries: Array.isArray(cfg.boundaries) ? cfg.boundaries.join('\n') : cfg.boundaries || '',
                    identity: cfg.identity,
                    mission: cfg.mission,
                  },
                });
              }
            }
          }}
        >
          <option value="">-- 不使用模板 --</option>
          {personaTemplates.map((t) => (
            <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
          ))}
        </select>
      </div>
      <div className="grid grid-2" style={{ gap: '14px' }}>
        <div>
          <label className="form-label">角色</label>
          <input
            value={state.persona.role}
            onChange={(e) => dispatch({ type: 'SET_PERSONA', payload: { role: e.target.value } })}
          />
        </div>
        <div>
          <label className="form-label">语气</label>
          <input
            value={state.persona.tone}
            onChange={(e) => dispatch({ type: 'SET_PERSONA', payload: { tone: e.target.value } })}
          />
        </div>
      </div>
      <div className="grid grid-2" style={{ gap: '14px' }}>
        <div>
          <label className="form-label">语言</label>
          <select
            value={state.persona.language}
            onChange={(e) => dispatch({ type: 'SET_PERSONA', payload: { language: e.target.value } })}
          >
            <option value="zh">中文</option>
            <option value="en">English</option>
            <option value="auto">自动</option>
          </select>
        </div>
        <div>
          <label className="form-label">边界 / 规则</label>
          <textarea
            value={state.persona.boundaries}
            onChange={(e) => dispatch({ type: 'SET_PERSONA', payload: { boundaries: e.target.value } })}
            placeholder="每行一条"
            rows={3}
          />
        </div>
      </div>
    </div>
  );

  const renderSkillsForm = () => (
    <div className="flex flex-col" style={{ gap: '12px' }}>
      {state.skills.map((skill, idx) => (
        <div key={idx} className="glass-card" style={{ padding: '14px' }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong>Skill #{idx + 1}</strong>
            <button className="icon-btn danger" onClick={() => {
              const next = state.skills.filter((_, i) => i !== idx);
              dispatch({ type: 'SET_SKILLS', payload: next });
            }}>
              <Trash2 size={16} />
            </button>
          </div>
          <div className="grid grid-2" style={{ gap: '10px' }}>
            <input
              placeholder="名称(name)"
              value={skill.name}
              onChange={(e) => {
                const next = [...state.skills];
                next[idx].name = e.target.value;
                dispatch({ type: 'SET_SKILLS', payload: next });
              }}
            />
            <input
              placeholder="显示名"
              value={skill.display_name}
              onChange={(e) => {
                const next = [...state.skills];
                next[idx].display_name = e.target.value;
                dispatch({ type: 'SET_SKILLS', payload: next });
              }}
            />
          </div>
          <input
            placeholder="描述"
            value={skill.description}
            onChange={(e) => {
              const next = [...state.skills];
              next[idx].description = e.target.value;
              dispatch({ type: 'SET_SKILLS', payload: next });
            }}
            style={{ marginTop: '10px' }}
          />
          <textarea
            placeholder="Skill 定义（JSON / YAML / 自然语言）"
            value={skill.definition}
            onChange={(e) => {
              const next = [...state.skills];
              next[idx].definition = e.target.value;
              dispatch({ type: 'SET_SKILLS', payload: next });
            }}
            rows={3}
            style={{ marginTop: '10px' }}
          />
        </div>
      ))}
      <button
        className="btn btn-secondary"
        onClick={() => dispatch({ type: 'SET_SKILLS', payload: [...state.skills, emptySkill()] })}
      >
        <Plus size={16} /> 添加 Skill
      </button>
    </div>
  );

  const renderToolsForm = () => (
    <div className="flex flex-col" style={{ gap: '14px' }}>
      <div>
        <label className="form-label">从工具库选择</label>
        <select
          onChange={(e) => {
            const tool = availableTools.find((t) => t.id === e.target.value);
            if (!tool || state.tools.some((t) => t.tool_id === tool.id)) return;
            dispatch({
              type: 'SET_TOOLS',
              payload: [...state.tools, { tool_id: tool.id, name: tool.name, description: tool.description, alias: tool.name, is_enabled: true }],
            });
            e.target.value = '';
          }}
        >
          <option value="">-- 选择工具 --</option>
          {availableTools.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
      </div>
      {state.tools.map((t, idx) => (
        <div key={idx} className="glass-card" style={{ padding: '12px' }}>
          <div className="flex" style={{ justifyContent: 'space-between' }}>
            <div>
              <strong>{t.name}</strong>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t.description}</div>
            </div>
            <button className="icon-btn danger" onClick={() => dispatch({ type: 'SET_TOOLS', payload: state.tools.filter((_, i) => i !== idx) })}>
              <X size={16} />
            </button>
          </div>
          <div style={{ marginTop: '10px' }}>
            <label className="form-label">别名</label>
            <input
              value={t.alias}
              onChange={(e) => {
                const next = [...state.tools];
                next[idx].alias = e.target.value;
                dispatch({ type: 'SET_TOOLS', payload: next });
              }}
            />
          </div>
        </div>
      ))}
      {state.tools.length === 0 && <div className="muted">尚未附加工具</div>}
    </div>
  );

  const renderKnowledgeForm = () => (
    <div className="flex flex-col" style={{ gap: '12px' }}>
      {state.knowledgeItems.map((item, idx) => (
        <div key={idx} className="glass-card" style={{ padding: '14px' }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong>知识 #{idx + 1}</strong>
            <button className="icon-btn danger" onClick={() => dispatch({ type: 'SET_KNOWLEDGE', payload: state.knowledgeItems.filter((_, i) => i !== idx) })}>
              <Trash2 size={16} />
            </button>
          </div>
          <input
            placeholder="分类"
            value={item.category}
            onChange={(e) => {
              const next = [...state.knowledgeItems];
              next[idx].category = e.target.value;
              dispatch({ type: 'SET_KNOWLEDGE', payload: next });
            }}
          />
          <textarea
            placeholder="知识内容"
            value={item.content}
            onChange={(e) => {
              const next = [...state.knowledgeItems];
              next[idx].content = e.target.value;
              dispatch({ type: 'SET_KNOWLEDGE', payload: next });
            }}
            rows={4}
            style={{ marginTop: '10px' }}
          />
        </div>
      ))}
      <button
        className="btn btn-secondary"
        onClick={() => dispatch({ type: 'SET_KNOWLEDGE', payload: [...state.knowledgeItems, { content: '', category: 'general' }] })}
      >
        <Plus size={16} /> 添加知识
      </button>
    </div>
  );

  const renderMcpForm = () => (
    <div className="flex flex-col" style={{ gap: '12px' }}>
      {state.mcpServers.map((s, idx) => (
        <div key={idx} className="glass-card" style={{ padding: '14px' }}>
          <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong>MCP Server #{idx + 1}</strong>
            <button className="icon-btn danger" onClick={() => dispatch({ type: 'SET_MCP', payload: state.mcpServers.filter((_, i) => i !== idx) })}>
              <Trash2 size={16} />
            </button>
          </div>
          <div className="grid grid-2" style={{ gap: '10px' }}>
            <input
              placeholder="名称"
              value={s.name}
              onChange={(e) => {
                const next = [...state.mcpServers];
                next[idx].name = e.target.value;
                dispatch({ type: 'SET_MCP', payload: next });
              }}
            />
            <input
              placeholder="命令，如 npx / uvx"
              value={s.command}
              onChange={(e) => {
                const next = [...state.mcpServers];
                next[idx].command = e.target.value;
                dispatch({ type: 'SET_MCP', payload: next });
              }}
            />
          </div>
          <input
            placeholder="参数，逗号分隔"
            value={s.args}
            onChange={(e) => {
              const next = [...state.mcpServers];
              next[idx].args = e.target.value;
              dispatch({ type: 'SET_MCP', payload: next });
            }}
            style={{ marginTop: '10px' }}
          />
          <textarea
            placeholder="环境变量，每行 KEY=VALUE"
            value={s.env}
            onChange={(e) => {
              const next = [...state.mcpServers];
              next[idx].env = e.target.value;
              dispatch({ type: 'SET_MCP', payload: next });
            }}
            rows={2}
            style={{ marginTop: '10px' }}
          />
        </div>
      ))}
      <button className="btn btn-secondary" onClick={() => dispatch({ type: 'SET_MCP', payload: [...state.mcpServers, emptyMcp()] })}>
        <Plus size={16} /> 添加 MCP Server
      </button>
    </div>
  );

  const renderStep3 = () => {
    const tabs = [
      { id: 'llm', label: 'LLM', icon: Cpu },
      { id: 'persona', label: '人设', icon: Bot },
      { id: 'skills', label: 'Skills', icon: Sparkles },
      { id: 'tools', label: '工具', icon: Wrench },
      { id: 'knowledge', label: '知识库', icon: BookOpen },
      { id: 'mcp', label: 'MCP', icon: Plug },
    ];
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '16px' }}>能力配置</h3>
        <div className="capability-tabs">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                className={`capability-tab ${state.capabilityTab === t.id ? 'active' : ''}`}
                onClick={() => dispatch({ type: 'SET_CAPABILITY_TAB', payload: t.id })}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </div>
        <div className="glass-card" style={{ padding: '20px', marginTop: '12px' }}>
          {state.capabilityTab === 'llm' && renderLLMForm()}
          {state.capabilityTab === 'persona' && renderPersonaForm()}
          {state.capabilityTab === 'skills' && renderSkillsForm()}
          {state.capabilityTab === 'tools' && renderToolsForm()}
          {state.capabilityTab === 'knowledge' && renderKnowledgeForm()}
          {state.capabilityTab === 'mcp' && renderMcpForm()}
        </div>
      </div>
    );
  };

  // ---------------- Step 4: Channels ----------------
  const renderStep4 = () => {
    const types = state.framework === 'ct_v4' ? CT_CHANNEL_TYPES : [{ id: 'webhook', name: 'Webhook', fields: ['endpoint', 'token'] }];
    return (
      <div className="animate-fade-in">
        <h3 style={{ marginBottom: '16px' }}>通道配置</h3>
        <div style={{ marginBottom: '14px' }}>
          <label className="form-label">添加通道</label>
          <select
            onChange={(e) => {
              const t = e.target.value;
              if (!t) return;
              dispatch({ type: 'SET_CHANNELS', payload: [...state.channels, emptyChannel(t)] });
              e.target.value = '';
            }}
          >
            <option value="">-- 选择通道类型 --</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col" style={{ gap: '12px' }}>
          {state.channels.map((ch, idx) => {
            const meta = types.find((t) => t.id === ch.type) || types[0];
            return (
              <div key={idx} className="glass-card" style={{ padding: '14px' }}>
                <div className="flex" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong>{meta.name} #{idx + 1}</strong>
                  <div className="flex" style={{ gap: '8px', alignItems: 'center' }}>
                    <label className="flex" style={{ gap: '6px', alignItems: 'center', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        checked={ch.enabled}
                        onChange={(e) => {
                          const next = [...state.channels];
                          next[idx].enabled = e.target.checked;
                          dispatch({ type: 'SET_CHANNELS', payload: next });
                        }}
                      />
                      启用
                    </label>
                    <button className="icon-btn danger" onClick={() => dispatch({ type: 'SET_CHANNELS', payload: state.channels.filter((_, i) => i !== idx) })}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div className="grid grid-2" style={{ gap: '10px' }}>
                  <input
                    placeholder="通道名称"
                    value={ch.name}
                    onChange={(e) => {
                      const next = [...state.channels];
                      next[idx].name = e.target.value;
                      dispatch({ type: 'SET_CHANNELS', payload: next });
                    }}
                  />
                  {meta.fields.includes('endpoint') && (
                    <input
                      placeholder="Endpoint / 回调地址"
                      value={ch.endpoint || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].endpoint = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                    />
                  )}
                  {meta.fields.includes('app_id') && (
                    <input
                      placeholder="App ID"
                      value={ch.app_id || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].app_id = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                    />
                  )}
                  {meta.fields.includes('app_secret') && (
                    <input
                      type="password"
                      placeholder="App Secret"
                      value={ch.app_secret || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].app_secret = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                    />
                  )}
                  {meta.fields.includes('token') && (
                    <input
                      type="password"
                      placeholder="Token / Bot Token"
                      value={ch.token || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].token = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                    />
                  )}
                  {meta.fields.includes('webhook_url') && (
                    <input
                      placeholder="Webhook URL"
                      value={ch.webhook_url || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].webhook_url = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                    />
                  )}
                  <input
                    type="number"
                    placeholder="Rate Limit"
                    value={ch.rate_limit || ''}
                    onChange={(e) => {
                      const next = [...state.channels];
                      next[idx].rate_limit = parseInt(e.target.value) || 0;
                      dispatch({ type: 'SET_CHANNELS', payload: next });
                    }}
                  />
                  {meta.fields.includes('extra') && (
                    <textarea
                      placeholder="额外配置 JSON"
                      value={ch.extra || ''}
                      onChange={(e) => {
                        const next = [...state.channels];
                        next[idx].extra = e.target.value;
                        dispatch({ type: 'SET_CHANNELS', payload: next });
                      }}
                      rows={2}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {state.channels.length === 0 && <div className="muted">暂无通道，可从上方添加</div>}
        </div>
      </div>
    );
  };

  // ---------------- Step 5: Review ----------------
  const renderStep5 = () => (
    <div className="animate-fade-in">
      <h3 style={{ marginBottom: '16px' }}>部署预览</h3>
      <div className="glass-card" style={{ padding: '20px' }}>
        <div className="review-row"><span>Agent</span><strong>{state.agentName}</strong></div>
        <div className="review-row"><span>框架</span><strong>{state.framework}</strong></div>
        <div className="review-row"><span>LLM</span><strong>{state.llmConfig.provider} / {state.llmConfig.model}</strong></div>
        <div className="review-row"><span>人设</span><strong>{state.persona.role || '未设置'}</strong></div>
        <div className="review-row"><span>Skills</span><strong>{state.skills.length}</strong></div>
        <div className="review-row"><span>工具</span><strong>{state.tools.length}</strong></div>
        <div className="review-row"><span>知识</span><strong>{state.knowledgeItems.length}</strong></div>
        <div className="review-row"><span>MCP</span><strong>{state.mcpServers.length}</strong></div>
        <div className="review-row"><span>通道</span><strong>{state.channels.length}</strong></div>
      </div>
      {state.deployError && (
        <div className="glass-card" style={{ marginTop: '14px', borderColor: 'var(--color-error)', color: 'var(--color-error)' }}>
          <AlertTriangle size={18} style={{ marginRight: '8px' }} />
          {state.deployError}
        </div>
      )}
    </div>
  );

  // ---------------- Step 6: Done ----------------
  const renderStep6 = () => (
    <div className="animate-fade-in" style={{ textAlign: 'center' }}>
      <div className="glass-card neon-border-green" style={{ padding: '48px' }}>
        {state.deployStatus === 'completed' ? (
          <>
            <Check size={64} style={{ color: 'var(--neon-green)', marginBottom: '16px' }} />
            <h2 style={{ color: 'var(--neon-green)' }}>Agent 创建完成</h2>
            <p style={{ color: 'var(--text-secondary)' }}>你随时可以前往 Agent 列表查看或继续编辑</p>
          </>
        ) : state.deployStatus === 'failed' ? (
          <>
            <AlertTriangle size={64} style={{ color: 'var(--color-error)', marginBottom: '16px' }} />
            <h2 style={{ color: 'var(--color-error)' }}>部署失败</h2>
            <p style={{ color: 'var(--text-secondary)' }}>{state.deployError || '请检查日志后重试'}</p>
          </>
        ) : (
          <>
            <Loader2 size={64} className="spin" style={{ marginBottom: '16px' }} />
            <h2>正在部署...</h2>
            <p style={{ color: 'var(--text-secondary)' }}>{state.deployStatus}</p>
          </>
        )}
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (state.step) {
      case 0: return renderStep0();
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  return (
    <div className="page-container wizard-page">
      <div className="page-header">
        <h1><Bot className="header-icon" /> 创建新 Agent</h1>
        <p>一句话直接创建、五步引导式建包，或通过向导完成基础信息、能力配置、通道与部署</p>
      </div>

      {/* 创建模式切换：一句话创建 / 引导式创建 / 表单向导 */}
      <div className="capability-tabs" style={{ marginBottom: '16px' }}>
        <button
          className={`capability-tab ${mode === 'oneshot' ? 'active' : ''}`}
          onClick={() => setMode('oneshot')}
        >
          <Sparkles size={16} /> 一句话创建
        </button>
        <button
          className={`capability-tab ${mode === 'guided' ? 'active' : ''}`}
          onClick={() => setMode('guided')}
        >
          <ListChecks size={16} /> 引导式创建
        </button>
        <button
          className={`capability-tab ${mode === 'wizard' ? 'active' : ''}`}
          onClick={() => setMode('wizard')}
        >
          <Bot size={16} /> 表单向导
        </button>
      </div>

      {mode === 'oneshot' ? (
        <div className="wizard-content glass-card">
          <AgentBuilderOneShot />
        </div>
      ) : mode === 'guided' ? (
        <div className="wizard-content glass-card">
          <GuidedBuildWizard />
        </div>
      ) : (
        <>
          {renderStepIndicator()}

          <div className="wizard-content glass-card">
            {renderStepContent()}
          </div>

          <div className="wizard-actions">
            {state.step > 0 && state.step < 6 && (
              <button className="btn btn-secondary" onClick={() => dispatch({ type: 'PREV_STEP' })} disabled={state.saving}>
                <ChevronLeft size={18} /> 上一步
              </button>
            )}
            {state.step < 5 && (
              <button className="btn btn-primary" onClick={handleNext} disabled={!canProceed() || state.saving}>
                {state.saving ? <Loader2 size={18} className="spin" /> : <>
                  下一步 <ChevronRight size={18} />
                </>}
              </button>
            )}
            {state.step === 5 && (
              <button className="btn btn-primary" onClick={async () => {
                dispatch({ type: 'NEXT_STEP' });
                await doDeploy();
              }} disabled={state.saving}>
                <Rocket size={18} /> 确认部署
              </button>
            )}
            {state.step === 6 && (
              <button className="btn btn-primary" onClick={() => state.agentId ? navigate(`/agents/${state.agentId}`) : navigate('/agents')}>
                {state.agentId ? '查看 Agent' : '完成，返回列表'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
